import { GROUNDED_PHONE_RULE } from '../core/contracts.js'

/**
 * Bind public research and Live googleSearch to the visitor's name + email/domain.
 * Prevents namesake hops and junk consulting sources. No per-company URL table.
 */

export interface IdentityBoundTarget {
  personName?: string | null
  companyName?: string | null
  domain?: string | null
  email?: string | null
  website?: string | null
  orgNumber?: string | null
  profileUrl?: string | null
  country?: string | null
  industry?: string | null
  currentWork?: string | null
  currentCompany?: string | null
}

export interface IdentityBoundCitation {
  uri: string
  title?: string
  description?: string
  /** Publication date from the source, ISO YYYY-MM-DD when known. */
  publishedAt?: string | null
  /** When this process retrieved the hit, ISO timestamp. */
  retrievedAt?: string
}

const JUNK_CONSULTING_HOSTS = new Set([
  'tracxn.com',
  'www.tracxn.com',
  'v0.dev',
  'v0.app',
  'www.v0.dev',
  'opencorporates.com',
  'www.opencorporates.com',
])

const NAMESAKE_HOST_MARKERS = [
  'evelegal',
  'eve-legal',
  'eve.legal',
  'woebot',
  'wysa',
]

const NAMESAKE_TITLE_MARKERS = [
  'eve legal',
  'jay madheswaran',
  'woebot',
  'wysa',
]

const FILMOGRAPHY_MARKERS = [
  'basshunter',
  'crash & burn',
  'crash and burn',
  'imdb.com',
]

const PROFESSIONAL_HOSTS = new Set([
  'linkedin.com',
  'www.linkedin.com',
  'proff.no',
  'www.proff.no',
  'brreg.no',
  'www.brreg.no',
  'w2.brreg.no',
  'data.brreg.no',
  'companieshouse.gov.uk',
  'www.companieshouse.gov.uk',
  'find-and-update.company-information.service.gov.uk',
  'allabolag.se',
  'www.allabolag.se',
  'sec.gov',
  'www.sec.gov',
])

const TOKEN_STOPWORDS = new Set([
  'as',
  'asa',
  'the',
  'to',
  'of',
  'and',
  'for',
  'inc',
  'llc',
  'ltd',
  'co',
  'ai',
  'company',
  'group',
  'ab',
  'oy',
  'gmbh',
])

const REGISTRY_SITES_BY_COUNTRY: Record<string, string[]> = {
  NO: ['https://www.proff.no', 'https://www.brreg.no'],
  GB: ['https://find-and-update.company-information.service.gov.uk'],
  UK: ['https://find-and-update.company-information.service.gov.uk'],
  US: ['https://www.sec.gov'],
  SE: ['https://www.allabolag.se'],
  AE: ['https://www.difc.ae'],
}

/** Static Live system rules — no specific visitor name (briefing stays in the seed). */
export const LIVE_VISITOR_COMPANY_LOCK_RULES = `VISITOR COMPANY LOCK:
- The bound visitor is the person (name + email/domain) in the session seed. Bind answers to that person.
- The email domain is a SEARCH KEY and the addressed org (it may be a past employer). Do not treat it as the person's current company if they stated a company in chat/dossier or search shows otherwise.
- If they name a company in this chat or the dossier (e.g. Proff Rørlegger AS), that company WINS for the COMPANY label and for orgnr / adresse / telefon / tjenester answers. Do not answer those from the email-domain org or from F.B/c.
- Person googleSearch queries include the visitor name AND that domain.
- Company-fact googleSearch query MUST include the stated/current company name — never F.B/c, never invent a phone number.
- Never answer as a namesake person or company (same short name, different legal entity, country, or founder).
- When asked what you found, prefer the official site, LinkedIn /in, and the local business registry for the visitor location (Proff / Brønnøysund for Norway, Companies House for the UK, etc.). Drop namesake Wikipedia, filmography, aggregator junk, and generic companies that do not match the bound person + domain.
- OWNER / CAPABILITY TEST: If they say they are testing, debugging, checking voice/webcam/screen/PDF, or identify as the product owner iterating — follow that protocol. Bound company research is context only. Do not redirect to that company's consulting workflows (content creation, chat analysis, problem interviews) until they ask to consult.`

export const LIVE_RESEARCH_SEED_PREAMBLE =
  'Bound visitor research from concierge (identity lock), for continuity only. Do not respond to this message — wait for me to speak next.'

export function normalizeHost(value: string | null | undefined): string {
  if (!value) return ''
  return value.replace(/^www\./, '').toLowerCase().trim()
}

export function hostnameFromUrl(uri: string | null | undefined): string {
  if (!uri) return ''
  try {
    return normalizeHost(new URL(uri).hostname)
  } catch {
    const match = uri.match(/https?:\/\/([^/]+)/i)
    return match?.[1] ? normalizeHost(match[1]) : ''
  }
}

export function domainFromEmail(email: string | null | undefined): string {
  const domain = email?.split('@')[1]?.toLowerCase().trim()
  return domain || ''
}

function tokens(value: string | null | undefined): string[] {
  return (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(token => token.length >= 2 && !TOKEN_STOPWORDS.has(token))
}

/** Distinctive tokens only — short words like "eve"/"to" must not match namesakes. */
export function distinctiveTokens(value: string | null | undefined): string[] {
  return tokens(value).filter(token => token.length >= 4)
}

function haystack(citation: IdentityBoundCitation): string {
  return `${citation.uri} ${citation.title || ''} ${citation.description || ''}`.toLowerCase()
}

export function isOfficialVisitorHost(host: string, domain: string | null | undefined): boolean {
  const bound = normalizeHost(domain)
  const h = normalizeHost(host)
  if (!bound || !h) return false
  return h === bound || h.endsWith(`.${bound}`)
}

const BUSINESS_REGISTRY_SUFFIXES = [
  'brreg.no',
  'proff.no',
  'companieshouse.gov.uk',
  'company-information.service.gov.uk',
  'allabolag.se',
  'sec.gov',
  'bolagsverket.se',
] as const

/** A registry row is identity, not research about the company. LinkedIn is not in this list. */
export function isBusinessRegistryHost(host: string | null | undefined): boolean {
  const normalized = normalizeHost(host)
  if (!normalized) return false
  return BUSINESS_REGISTRY_SUFFIXES.some(
    suffix => normalized === suffix || normalized.endsWith(`.${suffix}`),
  )
}

export function isProfessionalSourceHost(host: string): boolean {
  const h = normalizeHost(host)
  if (PROFESSIONAL_HOSTS.has(host) || PROFESSIONAL_HOSTS.has(h) || PROFESSIONAL_HOSTS.has(`www.${h}`)) {
    return true
  }
  return (
    h === 'linkedin.com' ||
    h.endsWith('.linkedin.com') ||
    h.endsWith('proff.no') ||
    h.endsWith('brreg.no') ||
    h.endsWith('companieshouse.gov.uk') ||
    h.endsWith('company-information.service.gov.uk') ||
    h.endsWith('allabolag.se') ||
    h.endsWith('sec.gov')
  )
}

function looksLikeFilmography(text: string): boolean {
  return FILMOGRAPHY_MARKERS.some(marker => text.includes(marker))
}

function looksLikeNamesake(text: string, host: string, domain: string): boolean {
  if (isOfficialVisitorHost(host, domain)) return false
  if (NAMESAKE_HOST_MARKERS.some(marker => host.includes(marker))) return true
  if (NAMESAKE_TITLE_MARKERS.some(marker => text.includes(marker))) return true
  return false
}

export function extractedCompanyAlignsWithVisitor(
  extractedName: string | null | undefined,
  domain: string | null | undefined,
  intakeLabel?: string | null,
  extractedWebsite?: string | null,
): boolean {
  const name = extractedName?.trim()
  if (!name) return false
  const hay = name.toLowerCase()
  if (NAMESAKE_TITLE_MARKERS.some(marker => hay.includes(marker))) return false
  if (looksLikeFilmography(hay)) return false

  const boundDomain = normalizeHost(domain)
  const websiteHost = hostnameFromUrl(extractedWebsite)
  if (
    websiteHost &&
    boundDomain &&
    !isOfficialVisitorHost(websiteHost, boundDomain) &&
    looksLikeNamesake(hay, websiteHost, boundDomain)
  ) {
    return false
  }
  if (isOfficialVisitorHost(websiteHost, boundDomain)) return true

  const boundTokens = distinctiveTokens(intakeLabel || boundDomain?.split('.')[0])
  const extractedTokens = distinctiveTokens(name)
  if (boundTokens.length > 0 && extractedTokens.some(token => boundTokens.includes(token))) {
    const slug = compactAlpha(boundDomain?.split('.')[0] || '')
    const compactExtracted = compactAlpha(name)
    const compactLabel = compactAlpha(intakeLabel || '')
    const nested =
      (compactLabel.length >= 6 &&
        (compactExtracted.includes(compactLabel) || compactLabel.includes(compactExtracted))) ||
      (slug.length >= 6 && (compactExtracted.includes(slug) || slug.includes(compactExtracted)))
    // "proff" overlaps PROFF AS and Proff Rørlegger. The domain slug has to nest.
    if (nested || (slug.length < 6 && compactLabel.length < 6)) return true
    return false
  }
  if (boundDomain && (hay.includes(boundDomain) || hay.includes(boundDomain.split('.')[0] || ''))) {
    return true
  }
  if (compactMatch(name, intakeLabel || boundDomain?.split('.')[0], boundDomain)) return true
  return false
}

export function isNamesakeCompanyExtract(
  extractedName: string | null | undefined,
  extractedWebsite?: string | null,
): boolean {
  const hay = `${extractedName || ''} ${extractedWebsite || ''}`.toLowerCase()
  const host = hostnameFromUrl(extractedWebsite)
  if (NAMESAKE_TITLE_MARKERS.some(marker => hay.includes(marker))) return true
  if (NAMESAKE_HOST_MARKERS.some(marker => host.includes(marker) || hay.includes(marker))) return true
  return looksLikeFilmography(hay)
}

/** Addressed org = email-domain company (possibly past). Reject namesakes; keep aligned legal names. */
export function pickBoundCompanyName(
  extractedName: string | null | undefined,
  intakeLabel: string,
  domain: string,
  extractedWebsite?: string | null,
): string {
  if (extractedCompanyAlignsWithVisitor(extractedName, domain, intakeLabel, extractedWebsite)) {
    return extractedName!.trim()
  }
  return intakeLabel
}

export function pickBoundCompanyDomain(
  extractedDomain: string | null | undefined,
  intakeDomain: string,
): string {
  const intake = normalizeHost(intakeDomain)
  if (intake) return intake
  return normalizeHost(extractedDomain)
}

export function pickBoundCompanyWebsite(
  extractedWebsite: string | null | undefined,
  domain: string,
): string | undefined {
  const host = hostnameFromUrl(extractedWebsite)
  if (extractedWebsite && isOfficialVisitorHost(host, domain)) return extractedWebsite.trim()
  const bound = normalizeHost(domain)
  if (bound) return `https://${bound}`
  const fallback = extractedWebsite?.trim()
  return fallback || undefined
}

/**
 * Current employer/role company from search.
 * If extract contradicts the email domain and is not a namesake, keep it (legacy email).
 */
export function pickCurrentWorkCompany(
  extractedPersonCompany: string | null | undefined,
  addressedCompany: string,
  domain: string,
  extractedWebsite?: string | null,
): string {
  const extracted = extractedPersonCompany?.trim()
  if (!extracted) return addressedCompany
  if (extractedCompanyAlignsWithVisitor(extracted, domain, addressedCompany, extractedWebsite)) {
    return extracted
  }
  if (isNamesakeCompanyExtract(extracted, extractedWebsite)) {
    return addressedCompany
  }
  return extracted
}

function wikipediaMatchesVisitor(
  citation: IdentityBoundCitation,
  target: IdentityBoundTarget,
): boolean {
  const text = haystack(citation)
  if (looksLikeFilmography(text)) return false
  const company = distinctiveTokens(target.companyName)
  const person = tokens(target.personName)
  const hasCompany = company.some(token => text.includes(token))
  const hasPerson = person.length === 0 || person.some(token => text.includes(token))
  return hasCompany && hasPerson
}

function compactAlpha(value: string): string {
  return value
    .replace(/[ØøÖö]/g, 'o')
    .replace(/[Ææ]/g, 'ae')
    .replace(/[ÅåÄä]/g, 'a')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

/** Glued email-domain label vs spaced registry title. */
function compactMatch(
  text: string,
  companyName: string | null | undefined,
  domain: string | null | undefined,
): boolean {
  const compactText = compactAlpha(text)
  if (compactText.length < 6) return false
  const compactCompany = compactAlpha(companyName || '')
  if (compactCompany.length >= 6 && compactText.includes(compactCompany)) return true
  const slug = (domain || '').split('.')[0] || ''
  return slug.length >= 6 && compactText.includes(slug)
}

const LINKEDIN_PROFILE_URL = /https?:\/\/(?:[\w.-]+\.)?linkedin\.com\/in\/[A-Za-z0-9_%-]+/i

/** Canonical /in URL from prose. Grounding redirect URIs do not contain the slug. */
export function linkedInProfileFromText(text: string | null | undefined): string | undefined {
  const match = text?.match(LINKEDIN_PROFILE_URL)
  if (!match?.[0]) return undefined
  const raw = match[0].replace(/[),.;]+$/, '')
  try {
    const url = new URL(raw)
    const slug = url.pathname.match(/\/in\/([^/?#]+)/i)?.[1]
    if (!slug) return undefined
    return `https://www.linkedin.com/in/${decodeURIComponent(slug)}`
  } catch {
    return undefined
  }
}

function linkedInMatchesVisitor(
  uri: string,
  target: IdentityBoundTarget,
): boolean {
  const path = uri.toLowerCase()
  if (path.includes('/company/')) {
    const distinctive = distinctiveTokens(target.companyName)
    if (distinctive.length === 0) return true
    return distinctive.some(token => path.includes(token.replace(/\s+/g, '-')))
  }
  if (!path.includes('/in/')) return false
  const person = tokens(target.personName)
  if (person.length === 0) return true
  const slug = person.join('-')
  return path.includes(slug) || person.every(token => path.includes(token))
}

export function isIdentityBoundCitation(
  citation: IdentityBoundCitation,
  target: IdentityBoundTarget,
): boolean {
  const uri = citation.uri?.trim()
  if (!uri) return false
  const host = hostnameFromUrl(uri)
  const domain = normalizeHost(target.domain) || domainFromEmail(target.email)
  const text = haystack(citation)

  if (JUNK_CONSULTING_HOSTS.has(host) || JUNK_CONSULTING_HOSTS.has(`www.${host}`)) return false
  if (looksLikeFilmography(text) && !isOfficialVisitorHost(host, domain)) return false
  if (looksLikeNamesake(text, host, domain)) return false

  if (isOfficialVisitorHost(host, domain)) return true
  if (host.includes('linkedin.com')) return linkedInMatchesVisitor(uri, target)
  // Unresolved grounding redirect: the host says nothing. Judge the /in URL quoted in the hit.
  if (host.endsWith('vertexaisearch.cloud.google.com')) {
    const quoted = linkedInProfileFromText(text)
    if (quoted) return linkedInMatchesVisitor(quoted, target)
  }
  if (isProfessionalSourceHost(host)) {
    const company = distinctiveTokens(target.companyName)
    if (company.length === 0) return true
    if (company.some(token => text.includes(token))) return true
    // "Proffrorlegger" vs "Proff Rørlegger AS" — registry pages space the legal name.
    return compactMatch(text, target.companyName, domain)
  }
  if (host.includes('wikipedia.org')) return wikipediaMatchesVisitor(citation, target)

  const company = distinctiveTokens(target.companyName)
  const person = tokens(target.personName)
  const mentionsCompany = company.some(token => text.includes(token))
  const mentionsPerson = person.length === 0 || person.some(token => text.includes(token))
  const mentionsDomain = domain ? text.includes(domain) : false
  // A full name in a third-party URL path is not identity: namesakes share it (N23).
  // A personal site whose HOST is the full name (farzadbayat.com) still binds.
  const compactHost = compactAlpha(host)
  const personalHost = person.length >= 2 && person.every(token => compactHost.includes(compactAlpha(token)))
  return (mentionsCompany && mentionsPerson) || mentionsDomain || personalHost
}

function rankScore(citation: IdentityBoundCitation, target: IdentityBoundTarget): number {
  const host = hostnameFromUrl(citation.uri)
  const domain = normalizeHost(target.domain) || domainFromEmail(target.email)
  if (isOfficialVisitorHost(host, domain)) return 0
  if (citation.uri.toLowerCase().includes('linkedin.com/in/')) return 1
  if (host.includes('linkedin.com')) return 2
  if (isProfessionalSourceHost(host) && !host.includes('linkedin.com')) return 3
  return 4
}

export function filterIdentityBoundCitations(
  citations: IdentityBoundCitation[],
  target: IdentityBoundTarget,
): IdentityBoundCitation[] {
  const seen = new Set<string>()
  const kept: IdentityBoundCitation[] = []
  for (const citation of citations) {
    if (!isIdentityBoundCitation(citation, target)) continue
    const key = citation.uri.trim()
    if (seen.has(key)) continue
    seen.add(key)
    kept.push(citation)
  }
  return kept.sort((a, b) => rankScore(a, target) - rankScore(b, target))
}

function canonicalLinkedInProfile(uri: string, target: IdentityBoundTarget): string | undefined {
  if (!/linkedin\.com\/in\//i.test(uri)) return undefined
  if (!linkedInMatchesVisitor(uri, target)) return undefined
  try {
    const url = new URL(uri)
    const match = url.pathname.match(/\/in\/([^/?#]+)/i)
    if (!match?.[1]) return undefined
    return `https://www.linkedin.com/in/${decodeURIComponent(match[1])}`
  } catch {
    const match = uri.match(/linkedin\.com\/in\/([^/?#]+)/i)
    if (match?.[1]) return `https://www.linkedin.com/in/${decodeURIComponent(match[1])}`
  }
  return undefined
}

export function extractLinkedInProfileUrl(
  citations: IdentityBoundCitation[],
  personName?: string | null,
): string | undefined {
  const target: IdentityBoundTarget = personName ? { personName } : {}
  for (const citation of citations) {
    const fromUri = citation.uri ? canonicalLinkedInProfile(citation.uri, target) : undefined
    if (fromUri) return fromUri
    // vertexaisearch grounding redirects never contain /in/{slug}. The slug is in the answer or title.
    const fromText = linkedInProfileFromText(
      [citation.title, citation.description, citation.uri].filter(Boolean).join(' '),
    )
    if (fromText && linkedInMatchesVisitor(fromText, target)) return fromText
  }
  return undefined
}

export function resolvePersonProfileUrl(
  rawProfileUrl: string | null | undefined,
  citations: IdentityBoundCitation[],
  personName?: string | null,
): string | undefined {
  const raw = rawProfileUrl?.trim()
  const target: IdentityBoundTarget = personName ? { personName } : {}
  if (raw && /linkedin\.com\/in\//i.test(raw) && linkedInMatchesVisitor(raw, target)) {
    return canonicalLinkedInProfile(raw, target) || raw
  }
  const fromRawText = raw ? linkedInProfileFromText(raw) : undefined
  if (fromRawText && linkedInMatchesVisitor(fromRawText, target)) return fromRawText
  return extractLinkedInProfileUrl(citations, personName)
}

function countryFromDomain(domain?: string | null): string {
  const host = normalizeHost(domain)
  const tld = host.split('.').pop() || ''
  if (tld === 'no') return 'NO'
  if (tld === 'uk') return 'GB'
  if (tld === 'se') return 'SE'
  if (tld === 'ae') return 'AE'
  return ''
}

export function preferredSearchSites(
  domain?: string | null,
  countryCode?: string | null,
): string[] {
  const sites = new Set<string>()
  const bound = normalizeHost(domain)
  if (bound) sites.add(`https://${bound}`)
  sites.add('https://www.linkedin.com')
  const code = (countryCode || countryFromDomain(domain)).toUpperCase()
  for (const url of REGISTRY_SITES_BY_COUNTRY[code] || []) {
    sites.add(url)
  }
  return Array.from(sites)
}

export function formatVisitorIdentityLock(target: IdentityBoundTarget): string {
  const domain = normalizeHost(target.domain) || domainFromEmail(target.email)
  const addressed = target.companyName?.trim() || null
  const currentCompany = target.currentCompany?.trim() || null
  const currentWork = target.currentWork?.trim() || null
  const currentDiffers =
    Boolean(currentCompany) &&
    Boolean(addressed) &&
    currentCompany!.toLowerCase() !== addressed!.toLowerCase()

  const lines = [
    'VISITOR IDENTITY LOCK — bind to this person (name + email/domain). Email domain is a search key, not a life sentence.',
    target.personName ? `- Person: ${target.personName}` : '',
    target.email ? `- Email: ${target.email}` : '',
    addressed ? `- Email-company (addressed org, possibly past): ${addressed}` : '',
    domain ? `- Domain: ${domain}` : '',
    currentDiffers ? `- Stated / current company (answer company-fact questions about THIS): ${currentCompany}` : '',
    currentDiffers ? `- Current work: ${currentWork ? `${currentWork} at ${currentCompany}` : currentCompany}` : '',
    !currentDiffers && currentWork ? `- Current work: ${currentWork}` : '',
    target.industry ? `- Industry: ${target.industry}` : '',
    target.website ? `- Official site: ${target.website}` : domain ? `- Official site: https://${domain}` : '',
    target.orgNumber ? `- Org number: ${target.orgNumber}` : '',
    target.country ? `- Country: ${target.country}` : '',
    target.profileUrl ? `- LinkedIn: ${target.profileUrl}` : '',
    domain
      ? currentDiffers
        ? `- Google Search: person queries include "${target.personName || addressed || domain}" and ${domain}. Company-fact queries (orgnr, adresse, telefon, tjenester) MUST include "${currentCompany}" — never F.B/c, never invent a phone.`
        : `- Google Search: every query MUST include "${target.personName || addressed || domain}" and ${domain}.`
      : '- Google Search: include the visitor name and email domain from this lock.',
    `- ${GROUNDED_PHONE_RULE}`,
    '- Prefer official site, LinkedIn /in, and the local registry from search. Drop namesake Wikipedia / Eve Legal / generic namesake companies. Filmography is not a consulting source.',
  ]
  return lines.filter(Boolean).join('\n')
}
